import { gql } from "@apollo/client";
import { formatCurrency } from "@asmbl/shared/money";
import { formatNumeral } from "@asmbl/shared/utils";
import makeStyles from "../../../makeStylesV4Compat";
import {
  calculateEquityFromVestEvents,
  filterVestEventsForVested,
  VestEvent,
} from "../../../models/EquityVestEvent";
import { GRAY_4 } from "../../../theme";
import {
  TotalVestedEquityChartHeader_equityGrantVestEvent as EquityGrantVestEvent,
  TotalVestedEquityChartHeader_equityHoldings as EquityHoldings,
} from "../../../__generated__/graphql";
import { AssembleTypography } from "../../AssembleTypography";
import { useCurrencies } from "../../CurrenciesContext";
import { useTotalEquitySlider } from "../../TotalEquitySlider/TotalEquitySliderContext";

const useStyles = makeStyles(() => ({
  element: {
    display: "flex",
    flexDirection: "column",
  },
  header: { color: GRAY_4 },
  cash: { fontWeight: 500 },
  units: { letterSpacing: "-1px" },
}));

type Props = {
  equityGrantVestEvents: EquityGrantVestEvent[];
  equityHoldings: EquityHoldings;
};

export function TotalVestedEquityChartHeader({
  equityGrantVestEvents,
  equityHoldings,
}: Props): JSX.Element {
  const date = new Date();
  const classes = useStyles();
  const { currencies } = useCurrencies();
  const equitySliderData = useTotalEquitySlider();

  const sharePriceTarget = equitySliderData?.value;

  const hasVestingData = equityGrantVestEvents.length > 0;

  const vestedVestEvents = filterVestEventsForVested(
    equityGrantVestEvents as VestEvent[],
    date
  );

  const vestedEquity = calculateEquityFromVestEvents(
    currencies,
    sharePriceTarget,
    vestedVestEvents,
    equityHoldings.valuationCurrency
  );

  return (
    <div className={classes.element}>
      <AssembleTypography
        className={classes.header}
        variant="productEyebrowMedium"
      >
        Vested
      </AssembleTypography>

      {hasVestingData ? (
        <>
          <AssembleTypography
            className={classes.cash}
            variant="productLongForm"
            data-cy="vested-equity-header-cash"
          >
            {vestedEquity.value !== null
              ? formatCurrency(vestedEquity.value, {
                  maximumFractionDigits: 0,
                })
              : "-"}
          </AssembleTypography>
          <AssembleTypography
            className={classes.units}
            variant="sidebarTitle"
            data-cy="vested-equity-header-units"
          >
            {`${formatNumeral(vestedEquity.count)} units`}
          </AssembleTypography>
        </>
      ) : (
        <AssembleTypography className={classes.cash} variant="productLongForm">
          No Data
        </AssembleTypography>
      )}
    </div>
  );
}

TotalVestedEquityChartHeader.fragments = {
  equityHoldings: gql`
    fragment TotalVestedEquityChartHeader_equityHoldings on EquityHoldings {
      id
      valuationCurrency {
        id
        code
        exchangeRate
      }
    }
  `,
  equityGrantVestEvent: gql`
    fragment TotalVestedEquityChartHeader_equityGrantVestEvent on EquityGrantVestEventSource {
      id
      vestingDate
      unitCount
      grant {
        id
        sharePrice
        unitCount
      }
    }
  `,
};
